import { ref, watch, computed } from '@vue/composition-api';
import store from '@/store';

// Notification
import { useToast } from 'vue-toastification/composition';
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue';
import useUtils from '@/composables/useUtils';

export default function useBookingList() {
  const toast = useToast();
  const { formatCurrency, convertUtcToLocalDate, formatDateDdMmYyyyHi, formatDateYyyyMmDd, convertDateToUTC } =
    useUtils();

  const perPageOptions = [10, 25, 50, 100];
  const tableColumns = [
    { key: 'voucherNum', sortable: true },
    { key: 'bookingType', label: 'type' },
    { key: 'bookingStatusId', label: 'status' },
    { key: 'cruiseLinerVessel', label: 'cruise' },
    { key: 'company', sortable: true },
    {
      key: 'totalBookingFee',
      label: 'total',
      sortable: true,
      formatter: (val) => `${formatCurrency(val)}`,
    },
    {
      key: 'bookedForDateUtc',
      label: 'booking date',
      sortable: true,
      sortable: true,
      formatter: (val) => `${formatDateDdMmYyyyHi(convertUtcToLocalDate(val))}`,
    },
    { key: 'actions' },
  ];

  const pageNumber = ref(1);
  const orderDesc = ref(true);
  const pageSize = ref(10);
  const refBookingListTable = ref(null);
  const orderBy = ref('id');
  const totalBookings = ref(0);
  
  const searchByDate = ref(localStorage.getItem('bookingList-searchByDate') == ''
      ? ''
      : localStorage.getItem('bookingList-searchByDate'));

  const companyFilter = ref(
    localStorage.getItem('bookingList-companyFilter') == 'null'
      ? null
      : JSON.parse(localStorage.getItem('bookingList-companyFilter'))
  );
  const bookingTypeFilter = ref(
    localStorage.getItem('bookingList-bookingTypeFilter') == 'null'
      ? null
      : JSON.parse(localStorage.getItem('bookingList-bookingTypeFilter'))
  );
  const bookingStatusFilter = ref(
    localStorage.getItem('bookingList-bookingStatusFilter') == 'null'
      ? null
      : JSON.parse(localStorage.getItem('bookingList-bookingStatusFilter'))
  );
  const searchQuery = ref(
    localStorage.getItem('bookingList-searchQuery') == ''
      ? ''
      : localStorage.getItem('bookingList-searchQuery')
  );

  const dataMeta = computed(() => {
    const localItemsCount = refBookingListTable.value
      ? refBookingListTable.value.localItems.length
      : 0;
    return {
      from: pageSize.value * (pageNumber.value - 1) + (localItemsCount ? 1 : 0),
      to: pageSize.value * (pageNumber.value - 1) + localItemsCount,
      of: totalBookings.value,
    };
  });

  watch(
    [
      pageNumber,
      pageSize,
      bookingTypeFilter,
      bookingStatusFilter,
      companyFilter,
      searchByDate,
    ],
    () => {
      localStorage.setItem(
        'bookingList-companyFilter',
        JSON.stringify(companyFilter.value)
      );
      localStorage.setItem(
        'bookingList-bookingTypeFilter',
        JSON.stringify(bookingTypeFilter.value)
      );
      localStorage.setItem(
        'bookingList-bookingStatusFilter',
        JSON.stringify(bookingStatusFilter.value)
      );
      localStorage.setItem(
        'bookingList-searchByDate',
        searchByDate.value
      );
      refBookingListTable.value.refresh();
    });

  const refreshTable = () => {
    localStorage.setItem(
        'bookingList-searchQuery',
        searchQuery.value
      );
    refBookingListTable.value.refresh();
  };

  const clearSearchByDate = () => {
    searchByDate.value = '';
    localStorage.setItem(
        'bookingList-searchQuery',
        searchQuery.value
      );
  };

  const fetchBookings = (ctx, callback) => {
    store
      .dispatch('booking/getBookings', {
        searchQuery: searchQuery.value === '' ? null : searchQuery.value,
        searchByDate: searchByDate.value === '' ? null : formatDateYyyyMmDd(convertDateToUTC(searchByDate.value)),
        pageSize: pageSize.value,
        pageNumber: pageNumber.value,
        orderBy: orderBy.value,
        orderDesc: orderDesc.value,
        bookingStatusId: bookingStatusFilter.value?.id ?? undefined,
        companyId: companyFilter.value?.id ?? undefined,
        bookingTypeId: bookingTypeFilter.value?.id ?? undefined,
      })
      .then((response) => {
        const { bookings, total } = response;
        callback(bookings);
        totalBookings.value = total;
      })
      .catch(() => {
        toast.error({
          component: ToastificationContent,
          props: {
            title: "Error fetching bookings' list",
            icon: 'AlertTriangleIcon',
          },
        });
      });
  };

  return {
    fetchBookings,
    tableColumns,
    pageSize,
    pageNumber,
    totalBookings,
    dataMeta,
    perPageOptions,
    searchQuery,
    orderBy,
    orderDesc,
    refBookingListTable,
    bookingStatusFilter,
    companyFilter,
    bookingTypeFilter,
    searchByDate,
    clearSearchByDate,
    refreshTable,
  };
}
